/**
 * CloseWorkflow — iter303ak-fix67 · F1 (P0 · reviewer-informed)
 *
 * Purpose: Real behaviour for the `close_workflow` primitive (2 usages
 * on the Closed / Declined final wrap actions). Previously fell through
 * to GenericFallback — the operator's "Close" click was silently
 * discarded, so runs stayed in "in_progress" forever.
 *
 * Behaviour: PATCHes the current workflow run's `status` to "closed"
 * with an optional `close_reason`. Advances the primitive on success
 * so the run reaches its terminal state cleanly.
 *
 * Params:
 *   header_title · card header · default "Close Workflow"
 *   label        · button label · default "Close this workflow"
 *   close_reason · optional — persisted with the close event
 */
import React, { useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function CloseWorkflow({ action, value, onChange, onNext, onBack, canBack, statusKey, capturedData, runId: runIdProp }) {
  const params = action.params || {};
  const headerTitle  = params.header_title || 'Close Workflow';
  const label        = params.label || 'Close this workflow';
  const closeReason  = params.close_reason || '';

  // W-B1 fix (2026-02): read runId from the prop the runner actually passes
  // (WorkflowRunnerPage.js:738). Falling back to capturedData is a legacy
  // path — the runner never populated __runId there, so the fallback POST
  // fired instead and 404'd because the endpoint queried the wrong fields.
  const runId = runIdProp || capturedData?.__runId || capturedData?.run_id || '';
  const [closing, setClosing] = useState(false);
  const [closed, setClosed]   = useState(!!value?.closed_at);
  const [err, setErr]         = useState('');

  const doClose = async () => {
    setErr(''); setClosing(true);
    try {
      // W-B1 fix: correct router prefix is `/workflow-runs` (hyphen), not
      // `/workflow/runs`. Slash prefix silently 404'd before.
      const body = {
        status: 'closed',
        close_reason: closeReason,
      };
      const { data } = runId
        ? await api.patch(`/workflow-runs/${runId}`, body)
        : await api.post('/workflow/side-effects/close-current-run', body);
      const payload = { closed_at: data?.closed_at || new Date().toISOString(), close_reason: closeReason, closed: true };
      setClosed(true);
      onChange(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Close failed');
    } finally { setClosing(false); }
  };

  return (
    <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm space-y-4"
         data-testid="wf-close-workflow">
      <div className="flex items-center gap-2 text-slate-800">
        <XCircle className="w-5 h-5" />
        <h3 className="font-semibold">{headerTitle}</h3>
      </div>

      {closeReason && (
        <div className="text-xs text-slate-600">
          <span className="font-medium">Reason:</span> {closeReason}
        </div>
      )}

      {!closed && (
        <button
          onClick={doClose}
          disabled={closing}
          data-testid="wf-close-workflow-btn"
          className="w-full px-6 py-4 border-2 border-rose-500 rounded font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-40"
        >
          {closing ? 'Closing…' : label}
        </button>
      )}

      {closed && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 flex items-start gap-2"
             data-testid="wf-close-workflow-done">
          <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-slate-800">Workflow closed</div>
            {statusKey && <div className="text-xs text-slate-600">Status: {statusKey}</div>}
          </div>
        </div>
      )}

      {err && (
        <div className="p-2 rounded border border-rose-200 bg-rose-50 text-xs text-rose-700 flex items-start gap-2"
             data-testid="wf-close-workflow-err">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          {err}
        </div>
      )}

      <div className="flex justify-between gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-close-workflow-back"
            className="px-4 py-2 rounded text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Back
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!closed}
          data-testid="wf-close-workflow-next"
          className="ml-auto px-6 py-2 rounded font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Finish →
        </button>
      </div>
    </div>
  );
}
